import { useState, useEffect, useMemo } from 'react';
import { validateEntitlements } from '../../../lib/helpers';
import { useNotificationContext } from '../../../providers/NotificationProvider';
import { NotificationErrors } from '../../../constants';

import {
    useDrBackupEntitlementsQuery,
    useBackupAppliancesQuery,
    useBackupPoliciesQuery,
    useLazyProtectionStatusQuery,
} from '../../../api';

export const useBackupData = ({
    userOrg,
    hideMigrationsBlock,
    setHideMigrationsBlock,
    hideBackupBlock,
    setHideBackupBlock,
    setHideDrBlock,
}) => {
    const { notifyError } = useNotificationContext();

    // Backups State
    const [policyBackupsInProgress, setPolicyBackupsInProgress] = useState(0);
    const [backupsInProgress, setBackupsInProgress] = useState(0);
    const [backupProtectedVms, setBackupProtectedVms] = useState(0);
    const [backupAppliances, setBackupAppliances] = useState(0);
    const [backupPolicies, setBackupPolicies] = useState(0);

    // Backup DR Entitlements State
    const [usedBackupDrEntitlements, setUsedBackupDrEntitlements] = useState(0);
    const [totalBackupDrEntitlements, setTotalBackupDrEntitlements] = useState(0);
    const [availableBackupDrEntitlements, setAvailableBackupDrEntitlements] = useState(0);

    // Query Hooks
    const { data: drBackupEntitlementsData, isLoading: isDrBackupEntitlementsLoading } = useDrBackupEntitlementsQuery(
        { userOrg },
        { skip: userOrg === null },
    );
    const { data: backupAppliancesData, isLoading: isBackupAppliancesLoading } = useBackupAppliancesQuery(
        { userOrg },
        { skip: userOrg === null },
    );
    const { data: backupPoliciesData, isLoading: isBackupPoliciesLoading } = useBackupPoliciesQuery(
        { userOrg },
        { skip: userOrg === null },
    );
    const [triggerGetProtectionStatus] = useLazyProtectionStatusQuery();

    const dataIsLoading = isBackupAppliancesLoading || isBackupPoliciesLoading || isDrBackupEntitlementsLoading;

    useEffect(() => {
        if (!dataIsLoading && userOrg) {
            getAllData();
        }
    }, [dataIsLoading, userOrg]);

    const getAllData = async () => {
        await Promise.all([getBackupPolicies(), getBackupAppliances(), getDrBackupEntitlements()]);
    };

    const backupData = useMemo(
        () => ({
            title: 'Backups',
            chartLabels: { completed: 'Completed Backups', available: 'Available Backups' },
            hideBlock: hideBackupBlock,
            used: usedBackupDrEntitlements,
            available: availableBackupDrEntitlements,
            total: totalBackupDrEntitlements,
            stats: [
                { title: 'Backups in Progress', link: '/backup_policies', value: policyBackupsInProgress },
                { title: 'Scheduled Backups', link: '/backup_policies', value: backupsInProgress },
                { title: 'Protected VMs', link: '/backup_policies', value: backupProtectedVms },
                { title: 'Backup Appliances', link: '/backup_appliance_setup', value: backupAppliances },
                {
                    title: 'Entitlement Status',
                    link: '/projects',
                    value: `${availableBackupDrEntitlements}/${totalBackupDrEntitlements}`,
                },
                { title: 'Backup Policies', link: '/backup_policies', value: backupPolicies },
            ],
        }),
        [
            hideBackupBlock,
            usedBackupDrEntitlements,
            availableBackupDrEntitlements,
            totalBackupDrEntitlements,
            policyBackupsInProgress,
            backupsInProgress,
            backupProtectedVms,
            backupAppliances,
            backupPolicies,
        ],
    );

    const getDrBackupEntitlements = () => {
        try {
            let entitlementBlocks = drBackupEntitlementsData?.content;
            let total;
            if (entitlementBlocks && entitlementBlocks.length > 0) {
                total = entitlementBlocks.reduce((accumulator, ent) => {
                    return accumulator + ent.max_protected_source_count;
                }, 0);

                let used = entitlementBlocks.reduce((accumulator, ent) => {
                    return accumulator + ent.protected_source_count;
                }, 0);

                let entitlementsAreValid = validateEntitlements(entitlementBlocks);
                let available = total - used;

                if (!entitlementsAreValid) {
                    setTotalBackupDrEntitlements(0);
                    setAvailableBackupDrEntitlements(0);
                }
                setUsedBackupDrEntitlements(used);
                setTotalBackupDrEntitlements(total);
                setAvailableBackupDrEntitlements(available);
            } else {
                if (hideMigrationsBlock) {
                    setHideMigrationsBlock(false);
                }
                setHideBackupBlock(true);
                setHideDrBlock(true);
            }
        } catch (error) {
            notifyError({ description: NotificationErrors.drEntitlements });
        }
    };

    const getBackupPolicies = () => {
        try {
            let policies = backupPoliciesData?.content;
            let backupPolicies = policies.length;
            let enabledPolicies = policies.filter((policy) => policy.is_active);
            let backupsInProgress = enabledPolicies.reduce((accumulator, policy) => {
                return accumulator + policy.source_ids.length;
            }, 0);

            // Display this value in the appropriate UI card
            setBackupsInProgress(backupsInProgress);
            setBackupPolicies(backupPolicies);

            // Get protection status for each policy
            getPolicyProtectionStatus(
                policies,
                setBackupProtectedVms,
                setPolicyBackupsInProgress,
                NotificationErrors.backupStatus,
            );
        } catch (error) {
            notifyError({ description: NotificationErrors.backupPolicies });
        }
    };

    const getBackupAppliances = () => {
        try {
            let backupAppliances = backupAppliancesData?.count;
            setBackupAppliances(backupAppliances);
        } catch (error) {
            notifyError({ description: NotificationErrors.backupAppliances });
        }
    };

    const getPolicyProtectionStatus = (policies, firstCardState, secondCardState, notificationError) => {
        // Get protection status for each policy
        let protectedVms = 0;
        let backupsInProgress = 0;
        if (policies.length > 0) {
            Promise.all(
                policies.map(async (policy) => {
                    try {
                        let data = await triggerGetProtectionStatus(
                            { policyId: policy.id },
                            { preferCachedValue: true },
                        ).unwrap();
                        let vms = data.content;
                        let protectedVirtualMachines = vms.filter((vm) => vm.last_successful_backup_id).length;
                        let inProgress = vms.filter((vm) => vm.running_backup_id).length;

                        return {
                            protectedVirtualMachines,
                            inProgress,
                        };
                    } catch (error) {
                        notifyError({ description: notificationError });
                        return { protectedVirtualMachines: 0, inProgress: 0 };
                    }
                }),
            ).then((results) => {
                protectedVms = results.reduce((sum, r) => sum + r.protectedVirtualMachines, 0);
                backupsInProgress = results.reduce((sum, r) => sum + r.inProgress, 0);

                firstCardState(protectedVms);
                secondCardState(backupsInProgress);
            });
        } else {
            firstCardState(0);
            secondCardState(0);
        }
    };

    return { backupData: dataIsLoading ? null : backupData };
};
